import React from 'react'
import { Link } from 'react-router-dom'

import {
  Avatar,
  Box,
  Button,
  Icon,
  StatusLabel,
  Tag,
  TagColor,
  Text,
  useToggle,
} from '@factorialco/deprecated-design-system'
import {
  FilterType,
  ProjectionKind,
  buildSoftwarePaymentsDataTableFilter,
  useSoftwarePaymentsDataTable,
} from 'generated/resources/datatable.graphql'
import {
  FinanceFinancialDocument,
  SoftwareManagementSoftwarePaymentFragment,
} from 'generated/resources/graphql'
import moment from 'moment'

import DataTable from 'modules/core/components/DataTable'
import Popover from 'modules/core/components/PopoverMenu'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { dateBackendDateTimeToUI, dateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'

import { invoiceStatusTagType, mapStatusText } from 'modules/finance/lib/invoices'
import AddSoftwarePaymentButton from 'modules/software_management/components/ApplicationDetailsContent/PaymentsWidget/AddSoftwarePaymentButton'
import EditPaymentModal from 'modules/software_management/components/ApplicationDetailsContent/PaymentsWidget/EditPaymentModal'
import { useApplicationDetailsRouterParams } from 'modules/software_management/hooks/useApplicationDetailsRouterParams'
import { cardPaymentStatusToColor, getSoftware } from 'modules/software_management/lib/software'
import { TABS } from 'modules/software_management/lib/tabs'

export const Spends = () => {
  const { navigation } = useNavigation()
  const [showEditModal, toggleEditModal] = useToggle(false)
  const [selectedPayment, setSelectedPayment] =
    React.useState<SoftwareManagementSoftwarePaymentFragment>()
  const {
    company,
    access: { locale },
  } = useCurrentSession()

  const { softwareSetup, currencies } = useApplicationDetailsRouterParams()

  const typeColorForSoftwarePayment = (
    cardPayment: { status: string } | null | undefined,
    invoice: { id: number; status: string } | null | undefined
  ) => {
    if (invoice) {
      return TagColor.malibu
    }
    return cardPayment ? TagColor.champagne : TagColor.viridian
  }

  const typeTextForSoftwarePayment = (
    cardPayment: { status: string } | null | undefined,
    invoice: { id: number; status: string } | null | undefined
  ) => {
    if (invoice) {
      return i18n.t('software_management.software_setups.tabs.spends.table.types.invoice')
    }
    return cardPayment
      ? i18n.t('software_management.software_setups.tabs.spends.table.types.card')
      : i18n.t('software_management.software_setups.tabs.spends.table.types.manual')
  }
  const source = useSoftwarePaymentsDataTable(
    [
      {
        identifier: 'name',
        title: i18n.t('software_management.software_setups.tabs.spends.table.name'),
        kind: ProjectionKind.Custom,
        render: ({ softwareSetup }) => {
          const software = getSoftware(softwareSetup)

          return (
            <Box flexDirection="row" alignItems="center" gap="s16">
              <Avatar label={String(software?.name)} url={software?.logoUrl} />
              <Text.Normal size="200">{software?.name}</Text.Normal>
            </Box>
          )
        },
        template: 'name',
      },
      {
        identifier: 'date',
        title: i18n.t('software_management.software_setups.tabs.spends.table.date'),
        kind: ProjectionKind.Custom,
        render: ({ executedAt }) => {
          return (
            <Text.Normal size="200">
              {executedAt ? dateBackendDateTimeToUI(executedAt) : ''}
            </Text.Normal>
          )
        },
        template: 'date',
      },
      {
        identifier: 'amount',
        title: i18n.t('software_management.software_setups.tabs.spends.table.amount'),
        kind: ProjectionKind.Custom,
        render: ({ amountCents, currency }) => {
          const amountWithCurrency = MoneyWithCurrency({
            amount: isNaN(amountCents) ? 0 : amountCents,
            currency,
            locale,
          })
          return <Text.Normal size="200">{amountWithCurrency}</Text.Normal>
        },
        template: 'date',
      },
      {
        identifier: 'type',
        title: i18n.t('software_management.software_setups.tabs.spends.table.type'),
        width: 200,
        kind: ProjectionKind.Custom,
        render: ({ financialDocument, cardPayment }) => {
          return (
            <Tag
              leftItem="circle"
              showCircle
              type={typeColorForSoftwarePayment(cardPayment, financialDocument)}
            >
              {typeTextForSoftwarePayment(cardPayment, financialDocument)}
            </Tag>
          )
        },
        template: 'date',
      },
      {
        identifier: 'status',
        width: 150,
        title: 'Status',
        kind: ProjectionKind.Custom,
        render: ({ cardPayment, financialDocument }) => {
          if (cardPayment) {
            return (
              <Tag
                leftItem="circle"
                inline
                showCircle
                type={cardPaymentStatusToColor(cardPayment.status)}
              >
                {cardPayment.status}
              </Tag>
            )
          } else if (financialDocument) {
            return (
              <StatusLabel type={invoiceStatusTagType(financialDocument.status)}>
                {mapStatusText(financialDocument.status)}
              </StatusLabel>
            )
          }
          return null
        },
        template: 'status',
      },
      {
        identifier: 'actions',
        kind: ProjectionKind.Decorative,
        width: 50,
        render: ({
          softwareSetup,
          id,
          cardPayment,
          financialDocument,
          amountCents,
          currency,
          executedAt,
        }) => {
          const paymentUrl = navigation.software.applications.applicationId.paymentId.fullPath({
            application_id: softwareSetup.id,
            paymentId: id,
          })

          if (cardPayment || financialDocument)
            return (
              <Box flexDirection="row" justifyContent="flexEnd">
                <Link to={`${paymentUrl}?tabs=${TABS.SPENDS}`} aria-label="payment-details">
                  <Icon.Medium icon="arrow-right" color={'grey600'} />
                </Link>
              </Box>
            )
          else
            return (
              <Box flexDirection="row" gap="s8" justifyContent="flexEnd">
                <Popover type="list">
                  <Popover.Trigger>
                    <Button.Decorative type="secondary" iconName="ellipsis" />
                  </Popover.Trigger>
                  <Popover.Content side="bottom" align="center">
                    <Popover.TouchableOption
                      icon="pencil"
                      title={i18n.t(
                        'software_management.software_setups.tabs.spends.table.actions.edit'
                      )}
                      onClick={() => {
                        setSelectedPayment({ id, amountCents, currency, executedAt })
                        toggleEditModal(true)()
                      }}
                    />
                  </Popover.Content>
                </Popover>
              </Box>
            )
        },
      },
    ],
    {
      variables: {
        companyId: company.id,
        softwareSetupId: softwareSetup.id,
      },
    }
  )

  const filterDate = buildSoftwarePaymentsDataTableFilter(source, {
    identifier: 'date',
    title: i18n.t('software_management.software_setups.tabs.spends.table.date'),
    path: 'executedAt',
    type: FilterType.Date,
    categories: [
      {
        kind: 'DatePicker',
        operation: 'between',
        value: [
          dateToString(moment().subtract(7, 'days'), 'backend_datetime'),
          dateToString(moment(), 'backend_datetime'),
        ],
      },
    ],
  })

  return (
    <Box paddingTop="s32" gap="s32">
      <DataTable
        source={source}
        exportFileName={i18n.t(
          'software_management.software_setups.tabs.spends.table.export_file_name'
        )}
        sort={{ path: 'id', direction: 'asc' }}
        actions={
          <AddSoftwarePaymentButton softwareSetupId={softwareSetup.id} currencies={currencies} />
        }
        filters={[filterDate]}
      />
      {showEditModal && selectedPayment && (
        <EditPaymentModal
          payment={selectedPayment}
          softwareSetupId={softwareSetup.id}
          currencies={currencies}
          onClose={toggleEditModal(false)}
          onSuccess={toggleEditModal(false)}
        />
      )}
    </Box>
  )
}
